import React, { useMemo, useCallback, useState, memo, Dispatch } from 'react';
import connect from 'react-redux/es/connect/connect';
import { useGoogleMap } from '../../context/GoogleMapContext';
import MapPolygon from './MapPolygon';
import MapOverlay from './MapOverlay';
import StyleConstants from '../../StyleConstants';
import { selectFieldHole, deselectFieldHole } from '../../reducers/FieldReducer';
import { Coordinate, CoordinateObject, Ring } from '../../../types/geo';

interface BoundingBox {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface MapFieldHoleProps {
  fieldHole: Ring;
  dispatch: Dispatch<any>;
  fieldHoleDisabled: boolean;
}

const mapStateToProps = (store: any) => ({
  selectedFieldHole: store.field.selectedFieldHole,
});

const MapFieldHole: React.FC<MapFieldHoleProps> = ({
  fieldHole,
  dispatch,
  selectedFieldHole,
  fieldHoleDisabled,
}) => {
  const map = useGoogleMap();
  const coordinates: CoordinateObject[] = fieldHole.map(
    ([lng, lat]: Coordinate): CoordinateObject => ({ lat, lng })
  );

  const [hovered, setHovered] = useState(false);

  const { strokeColor, strokeWeight } = useMemo((): {
    strokeColor: String;
    strokeWeight: number;
  } => {
    if (fieldHoleDisabled) return { strokeColor: 'rgb(224,210,61)', strokeWeight: 2 };
    if (hovered && selectedFieldHole !== fieldHole) {
      return {
        strokeColor: StyleConstants.palette.primary.main,
        strokeWeight: 4,
      };
    } else if (selectedFieldHole === fieldHole) {
      return {
        strokeColor: 'white',
        strokeWeight: 6,
      };
    }
    return { strokeColor: '', strokeWeight: 2 };
  }, [hovered, selectedFieldHole, fieldHole]);

  const getBoundingBox = useCallback((coords: { lat: number; lng: number }[]): BoundingBox => {
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    coords.forEach(({ lat, lng }) => {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    });

    return { minLat, maxLat, minLng, maxLng };
  }, []);

  const createCanvas = useCallback((width: number, height: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }, []);

  const drawPattern = useCallback((): HTMLCanvasElement => {
    const patternCanvas = document.createElement('canvas');
    const patternCtx = patternCanvas.getContext('2d');
    const stripeWidth = 4;
    const stripeSpacing = 4;

    patternCanvas.width = stripeWidth;
    patternCanvas.height = stripeWidth + stripeSpacing;

    patternCtx.fillStyle = 'rgba(51, 51, 51, 0.4)';
    patternCtx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);

    patternCtx.fillStyle = 'rgba(128, 128, 128, 0.8)';
    for (let y = 0; y < patternCanvas.height; y += stripeWidth + stripeSpacing) {
      patternCtx.fillRect(0, y, patternCanvas.width, stripeWidth);
    }

    return patternCanvas;
  }, []);

  const drawPolygon = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      projection: google.maps.MapCanvasProjection,
      coordinates: { lat: number; lng: number }[],
      topLeft: google.maps.Point,
      scale: number
    ): void => {
      const start = projection.fromLatLngToPoint(
        new window.google.maps.LatLng(coordinates[0].lat, coordinates[0].lng)
      );
      ctx.moveTo((start.x - topLeft.x) * scale, (start.y - topLeft.y) * scale);

      coordinates.forEach(({ lat, lng }) => {
        const point = projection.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
        ctx.lineTo((point.x - topLeft.x) * scale, (point.y - topLeft.y) * scale);
      });

      ctx.closePath();
      ctx.clip();
    },
    []
  );

  const drawOverlay = useCallback((): {
    dataUrl: string;
    bounds: window.google.maps.LatLngBounds;
  } => {
    if (!map || !coordinates.length || !map.getProjection()) return undefined;

    const boundingBox = getBoundingBox(coordinates);
    const projection = map.getProjection();

    const zoomLevel = 15;
    const scale = Math.pow(2, zoomLevel);

    const topLeft = projection.fromLatLngToPoint(
      new window.google.maps.LatLng(boundingBox.maxLat, boundingBox.minLng)
    );
    const bottomRight = projection.fromLatLngToPoint(
      new window.google.maps.LatLng(boundingBox.minLat, boundingBox.maxLng)
    );

    const width = (bottomRight.x - topLeft.x) * scale;
    const height = (bottomRight.y - topLeft.y) * scale;

    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');

    drawPolygon(ctx, projection, coordinates, topLeft, scale);

    const patternCanvas = drawPattern();
    ctx.fillStyle = ctx.createPattern(patternCanvas, 'repeat');
    ctx.fill();

    const dataUrl = canvas.toDataURL('image/png');
    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(boundingBox.minLat, boundingBox.minLng),
      new window.google.maps.LatLng(boundingBox.maxLat, boundingBox.maxLng)
    );

    return { dataUrl, bounds };
  }, [map, coordinates, getBoundingBox, createCanvas, drawPattern, drawPolygon]);

  const imageData = useMemo(
    (): { dataUrl: string; bounds: window.google.maps.LatLngBounds } => drawOverlay(),
    [drawOverlay]
  );

  const onMouseEnter: () => void = useCallback(() => setHovered(true), []);

  const onMouseLeave: () => void = useCallback(() => setHovered(false), []);

  const toggleFieldHoleSelection: () => void = useCallback(() => {
    if (selectedFieldHole === fieldHole) {
      dispatch(deselectFieldHole());
      return;
    }
    dispatch(selectFieldHole(fieldHole));
  }, [selectedFieldHole, fieldHole, dispatch]);

  return (
    <>
      <MapPolygon
        fillOpacity={0}
        fillColor={'white'}
        strokeColor={strokeColor}
        strokeWeight={strokeWeight}
        disabled={fieldHoleDisabled}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={toggleFieldHoleSelection}
        coordinates={coordinates}
        zIndex={0}
      />
      {imageData && (
        <MapOverlay
          zindex={999}
          fieldImage={{ url: imageData.dataUrl, bounds: imageData.bounds }}
        />
      )}
    </>
  );
};

export default memo(connect(mapStateToProps)(MapFieldHole));
